import React from 'react';
import { MoveRisk, MoveGroup } from './types';
import { useAltHotkeys } from '../../hooks/useAltHotkeys';
import { useModalDrag } from '../../hooks/useModalDrag';
import ModalHeader from '../ui/ModalHeader';

export interface RemoveConfirmModalProps {
  move: MoveRisk;
  group: MoveGroup | null;
  loading: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export function RemoveConfirmModal({ move, group, loading, onClose, onConfirm }: RemoveConfirmModalProps) {
  // Get all moves to display - if part of a group, show all
  const movesToRemove = group && group.isGroup ? group.moves : [move];
  const isMultipleMoves = movesToRemove.length > 1;

  // Rendered only while open. Alt+C closes; Remove gets no chord.
  useAltHotkeys({ C: onClose }, undefined, { layer: true });

  // Rendered only while open, so the drag offset resets on unmount.
  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(true);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-scrim/50">
      <div
        ref={panelRef}
        style={panelStyle}
        className="bg-panel rounded-lg shadow-xl max-w-md w-full mx-4"
      >
        {/* Header */}
        <ModalHeader
          title="Remove from Driver"
          onClose={loading ? undefined : onClose}
          dragHandleProps={dragHandleProps}
        />

        {/* Content */}
        <div className="px-6 py-4">
          <p className="text-label mb-4">
            Are you sure you want to remove {isMultipleMoves ? `these ${movesToRemove.length} linked moves` : 'this move'} from driver <span className="font-semibold">{move.driver}</span>?
          </p>

          {/* Move Details */}
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {movesToRemove.map((m, idx) => (
              <div key={`${m.pronumber}-${m.legnum}`} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-2">
                {isMultipleMoves && (
                  <div className="text-xs font-semibold text-purple-600 dark:text-purple-400 mb-2">
                    Leg {idx + 1} of {movesToRemove.length}
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-sm text-muted">PRO:</span>
                  <span className="text-sm font-medium text-body">{m.pronumber}-{m.legnum}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted">Type:</span>
                  <span className="text-sm font-medium text-body">{m.movetype}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted">Route:</span>
                  <span className="text-sm font-medium text-body">{m.origin_city_state} → {m.dest_city_state}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted">Deadline:</span>
                  <span className="text-sm font-medium text-body">{m.deadline_time}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted">Status:</span>
                  <span className="text-sm font-medium text-body">{m.status === 'Dispatched' ? 'Not Started' : m.status}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-label bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            disabled={loading}
          >
            {loading && <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />}
            {loading ? 'Removing...' : `Remove ${isMultipleMoves ? `${movesToRemove.length} Moves` : 'from Driver'}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default RemoveConfirmModal;
